








































































































































































import { Component, Mixins, Watch } from "vue-property-decorator";

import AppContent from "@/components/AppContent/AppContent.vue";

import AppHeaderBackWithTitle from "@/components/AppHeader/AppHeaderBackWithTitle.vue";
import AppHeader from "@/components/AppHeader/AppHeader.vue";
import InputRow from "@/components/InputRow/InputRow.vue";
import BaseDropdown from "@/components/BaseDropdown/BaseDropdown.vue";
import BaseButton from "@/components/BaseButton/BaseButton.vue";
import Spinner from "@/components/BaseSpinner/BaseSpinner.vue";
import Alert from "@/components/BaseAlert/BaseAlert.vue";

import { ValidationObserver, ValidationProvider } from "vee-validate";

import { REPORT_TYPES } from "@/models/constants/report-generator";
import { Action, State } from "vuex-class";
import { IVuexState } from "@/models/interfaces/store";
import { ITestCategory, ILevel, ISchool } from "@/models/interfaces/schools";
import { IStudent } from "@/models/interfaces/students";
import RequestState from "@/utils/mixins/RequestState";

import { ReportTypes } from "@/models/enums/report-generator";
import { ILesson } from "@/models/interfaces/lessons";
import { IClass } from "@/models/interfaces/classes";
import { isEmpty, reject, values } from "ramda";
import amplitude from "amplitude-js";
import { eventCodeByName, EventsNames } from "@/analytics";

@Component({
  components: {
    AppContent,
    AppHeaderBackWithTitle,
    AppHeader,
    InputRow,
    BaseDropdown,
    BaseButton,
    Spinner,
    Alert,
    ValidationObserver,
    ValidationProvider
  }
})
export default class ReportGenerator extends Mixins(RequestState) {
  @Action("schools/fetchSchools")
  private fetchSchools!: () => Promise<{ schools: ISchool[] }>;

  @Action("classes/fetchClassesForSchool")
  private fetchClassesForSchool!: (ids: string[]) => Promise<IClass[]>;

  @Action("students/fetchStudents")
  private fetchStudents!: () => Promise<IStudent[]>;

  @Action("lessons/fetchLessons")
  private fetchLessons!: (levelId?: string) => Promise<ILesson[]>;

  @Action("schools/fetchLevels")
  private fetchLevels!: (schoolsIds: string[]) => Promise<ILevel[]>;

  @Action("schools/fetchTestCategories")
  private fetchTestCategories!: (payload: {
    type: ReportTypes;
  }) => Promise<ITestCategory[]>;

  @State("schools", { namespace: "schools" })
  private schools!: IVuexState<ISchool[]>;

  @State("levels", { namespace: "schools" })
  private levels!: IVuexState<ILevel[]>;

  @State("classes", { namespace: "classes" })
  private classes!: IVuexState<IClass[]>;

  @State("students", { namespace: "students" })
  private students!: IVuexState<IStudent[]>;

  @State("lessons", { namespace: "lessons" })
  private lessons!: IVuexState<ILesson[]>;

  @State("testCategories", { namespace: "schools" })
  private testCategories!: IVuexState<ITestCategory[]>;

  private form = {
    level: "",
    schoolId: "",
    classId: "",
    subGroupId: "",
    reportType: "",
    lessonId: "",
    unit: "",
    examId: ""
  };

  private reportTypesItems = REPORT_TYPES;

  private get enableLevel() {
    const allowedTypes = [
      ReportTypes.Unit,
      ReportTypes.Level,
      ReportTypes.AssessmentReport,
      ReportTypes.BaselineTestReport,
      ReportTypes.ProgressReport
    ];
    return allowedTypes.includes(this.form.reportType as ReportTypes);
  }

  public get subGroups() {
    const group = this.classes.data?.find(
      item => item.classId === this.form.classId
    );

    return (
      group?.subGroups.map(subgroup => ({
        value: subgroup.subGroupId,
        label: subgroup.name
      })) ?? []
    );
  }

  private get enableExamCategory() {
    const allowedTypes = [
      ReportTypes.AssessmentReport,
      ReportTypes.BaselineTestReport
    ];
    return allowedTypes.includes(this.form.reportType as ReportTypes);
  }

  private get enableLesson() {
    return this.form.reportType === ReportTypes.Lesson;
  }

  private get enableUnit() {
    return this.form.reportType === ReportTypes.Unit;
  }

  private get computedUnits() {
    const selectedLevel = this.form.level;

    const units = this.levels.data?.find(
      item => item.levelId === Number(selectedLevel)
    )?.units;

    return !selectedLevel
      ? []
      : values(units || {})
          .map(unitArr => unitArr[0])
          .map(unit => ({
            value: unit.unitId,
            label: unit.title
          }));
  }

  private get computedSchools() {
    return this.schools.data!.map(item => {
      return {
        value: item.schoolId,
        label: item.schoolName
      };
    });
  }

  private get computedLevels() {
    return this.levels.data!.map(item => ({
      value: item.levelId,
      label: item.title
    }));
  }

  private get lessonsItems() {
    return this.lessons.data!.map(item => {
      return {
        value: item.lessonId,
        label: item.title
      };
    });
  }

  private get testCategoriesItems() {
    return this.testCategories.data?.map(item => ({
      value: item.category,
      label: item.description
    }));
  }

  private get computedClasses() {
    return this.classes.data?.map(item => {
      return {
        value: item.classId,
        label: item.className
      };
    });
  }

  private onSchoolSelected() {
    this.fetchClassesForSchool([this.form.schoolId]);
    this.fetchLevels([this.form.schoolId]);
  }

  private onReportTypeChanged() {
    const { reportType } = this.form;

    if (reportType !== ReportTypes.Lesson) {
      this.form = Object.assign(this.form, {
        lessonId: ""
      });
    }

    if (reportType !== ReportTypes.Level) {
      this.form = Object.assign(this.form, {
        level: ""
      });
    }

    if (
      reportType === ReportTypes.AssessmentReport ||
      reportType === ReportTypes.BaselineTestReport
    ) {
      this.fetchTestCategories({
        type: reportType as ReportTypes
      });
    }
  }

  private generateReport(validateForm: () => void, isValid: boolean) {
    validateForm();
    const query = reject(isEmpty, {
      ...this.form,
      studentCount: `${this.classes.data?.find(
        item => item.classId === this.form.classId
      )?.studentCount ?? 0}`
    });

    if (isValid) {
      amplitude
        .getInstance()
        .logEvent(
          `${eventCodeByName(EventsNames.UserClicksOnGenerateReport)} ${
            EventsNames.UserClicksOnGenerateReport
          }`
        );
      this.$router.push({
        path: "/report-generator/result",
        query
      });
    }
  }

  private created() {
    this.fetchSchools();
    this.fetchLessons();
    this.fetchStudents();
  }
}
